import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { IMG } from "../lib/assets";

const MEN_LINKS = [
  ["Shop All Men", "/shop?gender=men"],
  ["New Arrivals", "/shop?gender=men&sort=newest"],
  ["T-Shirts", "/shop?gender=men&category=tshirts"],
  ["Shirts", "/shop?gender=men&category=shirts"],
  ["Sweatshirts & Hoodies", "/shop?gender=men&category=sweatshirts"],
  ["Baggy Jeans & Denim", "/shop?gender=men&category=baggy-jeans"],
  ["Trousers", "/shop?gender=men&category=trousers"],
  ["Accessories", "/shop?gender=men&category=accessories"],
  ["Best Sellers", "/shop?gender=men&tag=bestseller"],
];

const WOMEN_LINKS = [
  ["Shop All Women", "/shop?gender=women"],
  ["New Arrivals", "/shop?gender=women&sort=newest"],
  ["T-Shirts & Tops", "/shop?gender=women&category=tshirts"],
  ["Shirts", "/shop?gender=women&category=shirts"],
  ["Sweatshirts & Hoodies", "/shop?gender=women&category=sweatshirts"],
  ["Baggy Jeans & Denim", "/shop?gender=women&category=baggy-jeans"],
  ["Trousers", "/shop?gender=women&category=trousers"],
  ["Accessories", "/shop?gender=women&category=accessories"],
  ["Best Sellers", "/shop?gender=women&tag=bestseller"],
];

const COLLECTION_LINKS = [
  ["T-Shirts", "/shop?category=tshirts"],
  ["Sweatshirts", "/shop?category=sweatshirts"],
  ["Baggy Jeans", "/shop?category=baggy-jeans"],
  ["Trousers", "/shop?category=trousers"],
  ["Shirts", "/shop?category=shirts"],
  ["Accessories", "/shop?category=accessories"],
];

const MEGA = { men: MEN_LINKS, women: WOMEN_LINKS, collections: COLLECTION_LINKS };

export function Wordmark({ compact = false }) {
  return (
    <Link to="/" data-testid="nav-logo-link" className="flex items-center justify-center select-none">
      <img src="/logo.png" alt="Velour & Vane" className={`w-auto ${compact ? "h-8" : "h-10 md:h-11"}`} />
    </Link>
  );
}

function MegaPanel({ mega }) {
  const links = MEGA[mega];
  const title = mega === "men" ? "Men" : mega === "women" ? "Women" : "Collections";
  const shopAll = mega === "collections" ? "/shop" : links[0][1];
  return (
    <motion.div data-testid={`mega-menu-${mega}`}
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="absolute left-0 right-0 top-full bg-white border-b border-vv-black/10 hidden lg:block">
      <div className="max-w-[1600px] mx-auto px-16 py-10 grid grid-cols-[1fr_280px] gap-16">
        <div>
          <p className="vv-overline text-vv-muted mb-6">{title}</p>
          <div className="grid grid-cols-3 gap-x-10 gap-y-4">
            {links.map(([label, to]) => (
              <Link key={label} data-testid={`mega-link-${mega}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={to}
                className="vv-link-underline w-fit text-sm text-vv-muted hover:text-vv-black transition-colors">
                {label}
              </Link>
            ))}
          </div>
        </div>
        <Link to={shopAll} data-testid={`mega-featured-${mega}`} className="group block">
          <div className="aspect-[4/5] overflow-hidden bg-vv-elevated">
            <img src={mega === "women" ? IMG.womenCampaign : IMG.tshirts} alt="" loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] mt-3 text-vv-muted group-hover:text-vv-black transition-colors">
            Collection 01 — Shop Now
          </p>
        </Link>
      </div>
    </motion.div>
  );
}

function MobileSection({ id, title, links, expanded, setExpanded }) {
  const open = expanded === id;
  return (
    <div className="border-b border-vv-black/5">
      <button data-testid={`mobile-nav-${id}-toggle`} onClick={() => setExpanded(open ? null : id)}
        className="w-full flex items-center justify-between py-4">
        <span className="font-display text-2xl">{title}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden">
            <div className="pb-5 flex flex-col">
              {links.map(([label, to]) => (
                <Link key={label} data-testid={`mobile-nav-${id}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={to}
                  className="py-2.5 pl-2 text-sm text-vv-muted hover:text-vv-black">
                  {label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { count, setCartOpen, wishlist, announcement, offers, user } = useShop();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mega, setMega] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setMenuOpen(false); setSearchOpen(false); setMega(null); setExpanded(null); }, [location.pathname, location.search]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  const navBtn = (id, label) => (
    <button key={id} data-testid={`nav-${id}`} onMouseEnter={() => setMega(id)}
      className={`text-[11px] uppercase tracking-[0.2em] font-medium py-2 whitespace-nowrap transition-colors ${mega === id ? "text-vv-black" : "text-vv-muted hover:text-vv-black"}`}>
      {label}
    </button>
  );

  return (
    <>
      <div data-testid="announcement-bar" className="bg-vv-black text-vv-ivory text-center py-2 px-4 text-[10px] tracking-[0.25em] uppercase relative z-[60]">
        {announcement}
        {offers.length > 0 && (
          <span data-testid="announcement-offers" className="text-vv-champagne">
            {offers.map((o) => `  ·  ${o.code}: ${o.type === "percent" ? `${o.value}% OFF` : `₹${o.value} OFF`}${o.min_order ? ` ABOVE ₹${Number(o.min_order).toLocaleString("en-IN")}` : ""}`)}
          </span>
        )}
      </div>
      <header data-testid="main-header" onMouseLeave={() => setMega(null)}
        className="sticky top-0 z-50 bg-white border-b border-vv-black/10 text-vv-black transition-colors duration-300">
        <div className="relative px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto grid grid-cols-[1fr_auto_1fr] items-center h-16 md:h-20">
          <div className="flex items-center gap-6">
            <button data-testid="mobile-menu-button" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="Menu">
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <nav className="hidden lg:flex items-center gap-8">
              {navBtn("men", "Men")}
              {navBtn("women", "Women")}
            </nav>
          </div>
          <Wordmark />
          <div className="flex items-center justify-end gap-4 md:gap-6">
            <nav className="hidden lg:flex items-center gap-5 mr-3">
              <Link data-testid="nav-new-arrivals" to="/shop?sort=newest" onMouseEnter={() => setMega(null)}
                className="vv-link-underline text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap">New Arrivals</Link>
              <Link data-testid="nav-best-sellers" to="/shop?tag=bestseller" onMouseEnter={() => setMega(null)}
                className="vv-link-underline text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap">Best Sellers</Link>
              {navBtn("collections", "Collections")}
            </nav>
            <button data-testid="nav-search-button" onClick={() => setSearchOpen(true)} aria-label="Search">
              <Search size={18} strokeWidth={1.5} />
            </button>
            <Link data-testid="nav-account-link" to={user ? "/account" : "/auth"} aria-label="Account" className="hidden sm:block">
              <User size={18} strokeWidth={1.5} />
            </Link>
            <Link data-testid="nav-wishlist-link" to="/account?tab=wishlist" aria-label="Wishlist" className="hidden sm:block relative">
              <Heart size={18} strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-vv-burgundy text-vv-ivory text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center">{wishlist.length}</span>
              )}
            </Link>
            <button data-testid="nav-cart-button" onClick={() => setCartOpen(true)} aria-label="Bag" className="relative">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 bg-vv-black text-vv-ivory text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
          <AnimatePresence>
            {mega && <MegaPanel mega={mega} />}
          </AnimatePresence>
        </div>
      </header>

      <AnimatePresence>
        {searchOpen && (
          <motion.div data-testid="search-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-white/95 backdrop-blur-md flex flex-col items-center pt-32 px-6">
            <button data-testid="search-close-button" className="absolute top-6 right-6" onClick={() => setSearchOpen(false)} aria-label="Close search">
              <X size={24} strokeWidth={1.25} />
            </button>
            <form onSubmit={submitSearch} className="w-full max-w-2xl">
              <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search the collection…"
                className="w-full bg-transparent border-b border-vv-black/30 focus:border-vv-black outline-none font-display text-3xl md:text-5xl py-4 placeholder:text-vv-stone" />
              <button data-testid="search-submit-button" type="submit" className="vv-btn-outline-dark mt-8">Search</button>
            </form>
          </motion.div>
        )}
        {menuOpen && (
          <motion.div data-testid="mobile-menu" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[70] bg-white flex flex-col">
            <div className="flex items-center justify-between px-6 h-16 border-b border-vv-black/10 shrink-0">
              <Wordmark compact />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <X size={22} strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-4 overflow-y-auto flex-1">
              <MobileSection id="men" title="Men" links={MEN_LINKS} expanded={expanded} setExpanded={setExpanded} />
              <MobileSection id="women" title="Women" links={WOMEN_LINKS} expanded={expanded} setExpanded={setExpanded} />
              <MobileSection id="collections" title="Collections" links={COLLECTION_LINKS} expanded={expanded} setExpanded={setExpanded} />
              <Link data-testid="mobile-nav-new-arrivals" to="/shop?sort=newest" className="font-display text-2xl py-4 border-b border-vv-black/5">New Arrivals</Link>
              <Link data-testid="mobile-nav-best-sellers" to="/shop?tag=bestseller" className="font-display text-2xl py-4 border-b border-vv-black/5">Best Sellers</Link>
              <Link data-testid="mobile-nav-our-story" to="/story" className="font-display text-2xl py-4 border-b border-vv-black/5">Our Story</Link>
              <Link data-testid="mobile-nav-account" to={user ? "/account" : "/auth"} className="mt-8 mb-10 text-[11px] uppercase tracking-[0.25em] text-vv-muted">
                {user ? "My Account" : "Sign In"}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
